import { type ReactNode } from "react";
import { Stack } from "@mui/material";
import type { FormikConfig, FormikProps } from "formik";

import type {
  CollectCriteriaDemographicsProps,
  ValidationSchema,
} from "./CollectCriteriaDemographics.js";
import { CollectCriteriaDemographics } from "./CollectCriteriaDemographics.js";
import type { CollectCriteriaNhsNumberProps } from "./CollectCriteriaNhsNumber.js";
import { CollectCriteriaNhsNumber } from "./CollectCriteriaNhsNumber.js";
import type { CollectCriteriaPdsDemographicsProps } from "./CollectCriteriaPdsDemographics.js";
import { CollectCriteriaPdsDemographics } from "./CollectCriteriaPdsDemographics.js";
import type { FooterProps } from "./Footer.js";

export type CriteriaType = "nhs-number" | "demographics" | "pds-demographics";

export type CollectCriteriaProps = {
  canUsePhoneticNameMatch: boolean;
  onSubmitPdsDemographics: CollectCriteriaPdsDemographicsProps["onSubmit"];
  onSubmitNhsNumber: CollectCriteriaNhsNumberProps["onSubmit"];
  onSubmitDemographics: CollectCriteriaDemographicsProps["onSubmit"];
  children?: ((args: Pick<FooterProps, "onClear">) => ReactNode) | ReactNode;
  criteriaType: CriteriaType;
  disabled?: boolean;
  lastSearchedDemographics: ValidationSchema | undefined;
};

// Turn a strongly-typed Formik<T> render function into
// a loosely-typed render function for consumers who don't
// care about field-specific values.
function mappedChildren<T>(
  children: NonNullable<CollectCriteriaProps["children"]>,
): FormikConfig<T>["children"] {
  if (typeof children !== "function") {
    return children as ReactNode;
  } else {
    return (args: FormikProps<T>) => {
      return children({
        onClear: () => {
          args.resetForm();
          args.setErrors({});
        },
      });
    };
  }
}

export const CollectCriteria = ({
  canUsePhoneticNameMatch,
  onSubmitNhsNumber,
  onSubmitDemographics,
  onSubmitPdsDemographics,
  children,
  criteriaType,
  disabled,
  lastSearchedDemographics,
}: CollectCriteriaProps) => {
  return (
    <Stack>
      {criteriaType === "nhs-number" && (
        <CollectCriteriaNhsNumber
          onSubmit={onSubmitNhsNumber}
          disabled={disabled}
        >
          {children && mappedChildren(children)}
        </CollectCriteriaNhsNumber>
      )}
      {criteriaType === "demographics" && (
        <CollectCriteriaDemographics
          canUsePhoneticNameMatch={canUsePhoneticNameMatch}
          onSubmit={onSubmitDemographics}
          disabled={disabled}
        >
          {children && mappedChildren(children)}
        </CollectCriteriaDemographics>
      )}
      {criteriaType === "pds-demographics" && (
        <CollectCriteriaPdsDemographics
          lastSearchedDemographics={lastSearchedDemographics}
          onSubmit={onSubmitPdsDemographics}
          disabled={disabled}
        >
          {children && mappedChildren(children)}
        </CollectCriteriaPdsDemographics>
      )}
    </Stack>
  );
};
